import { put, SagaGenerator, select, takeEvery } from "typed-redux-saga";
import { produce } from "immer";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Attachment } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { assertTrue } from "../../../utilities/assert";

import { isArchiveModalDocumentData, TypedAttachment } from "../archiveModalTypes";
import { ATTACHMENT_TYPE } from "../constants";

export function* addItemToSortingSaga(attachments: TypedAttachment[], activityId: string): SagaGenerator<void> {
	const generatedDocumentsById: Record<string, Attachment> = attachments
		.filter(it => it.type === "GENERIERT")
		.map(it => it.attachment)
		.filter((it: Attachment): it is Attachment & { attachment_id: string } => !!it.attachment_id)
		.reduce((prev, cur) => ({ ...prev, [cur.attachment_id]: cur }), {});
	const attachmentsById: Record<string, Attachment> = attachments
		.filter(it => it.type === "ATTACHMENT")
		.map(it => it.attachment)
		.filter((it: Attachment): it is Attachment & { attachment_id: string } => !!it.attachment_id)
		.reduce((prev, cur) => ({ ...prev, [cur.attachment_id]: cur }), {});
	const initialAttachmentsById: Record<string, Attachment> = attachments
		.filter(it => it.type === ATTACHMENT_TYPE.INITIAL)
		.map(it => it.attachment)
		.filter((it: Attachment): it is Attachment & { attachment_id: string } => !!it.attachment_id)
		.reduce((prev, cur) => ({ ...prev, [cur.attachment_id]: cur }), {});
	yield* takeEvery(ActivityActions.setData, function* (action) {
		if (action.payload.activityId !== activityId) {
			return;
		}
		const data = action.payload.data;
		assertTrue(isArchiveModalDocumentData(data));
		const selectedAttachments = data.document.root.attachments?.map(it => it.value) ?? [];
		const updatedSortingAttachments = data.document.root.sortierungAttachments?.map(it => it.id) ?? [];
		const updateSortingGeneratedDocuments = data.document.root.sortierungGeneratedDocuments?.map(it => it.id) ?? [];
		const selectedInitialAttachments = data.document.root.initialAttachments?.map(it => it.value) ?? [];
		const updatedSortingInitialAttachments =
			data.document.root.sortierungInitialAttachments
				?.map(it => it.id)
				.filter(id => initialAttachmentsById[id] !== undefined) ?? [];

		// Early return to prevent infinite recursion with setData
		if (
			selectedAttachments.filter(
				it => !updatedSortingAttachments.includes(it) && !updateSortingGeneratedDocuments.includes(it)
			).length === 0 &&
			[...updatedSortingAttachments, ...updateSortingGeneratedDocuments].filter(
				it => !selectedAttachments.includes(it)
			).length === 0 &&
			selectedInitialAttachments.every(it => updatedSortingInitialAttachments.includes(it)) &&
			updatedSortingInitialAttachments.every(it => selectedInitialAttachments.includes(it))
		) {
			return;
		}
		const stateData = yield* select(ActivitySelectors.data(activityId));
		assertTrue(isArchiveModalDocumentData(stateData));
		const currentSortingAttachments = stateData.document.root.sortierungAttachments?.map(it => it.id) ?? [];
		const currentGeneratedDocuments = stateData.document.root.sortierungGeneratedDocuments?.map(it => it.id) ?? [];
		const currentInitialAttachments = stateData.document.root.sortierungInitialAttachments?.map(it => it.id) ?? [];
		const notAddedAttachments = selectedAttachments.filter(
			it => !currentSortingAttachments.includes(it) && !currentGeneratedDocuments.includes(it)
		);
		const removedAttachments = [...currentSortingAttachments, ...currentGeneratedDocuments].filter(
			it => !selectedAttachments.includes(it)
		);
		const addedInitialAttachments = selectedInitialAttachments.filter(
			it => !currentInitialAttachments.includes(it)
		);
		const removedInitialAttachments = currentInitialAttachments.filter(
			it => initialAttachmentsById[it] !== undefined && !selectedInitialAttachments.includes(it)
		);
		if (
			notAddedAttachments.length === 0 &&
			removedAttachments.length === 0 &&
			addedInitialAttachments.length === 0 &&
			removedInitialAttachments.length === 0
		) {
			return;
		}
		const updatedDocument = produce(stateData, draft => {
			if (!draft.document.root.sortierungAttachments) {
				draft.document.root.sortierungAttachments = [];
			}
			if (!draft.document.root.sortierungGeneratedDocuments) {
				draft.document.root.sortierungGeneratedDocuments = [];
			}
			if (!draft.document.root.sortierungInitialAttachments) {
				draft.document.root.sortierungInitialAttachments = [];
			}
			for (const attachmentId of notAddedAttachments) {
				if (generatedDocumentsById[attachmentId] !== undefined) {
					const attachment = generatedDocumentsById[attachmentId];
					draft.document.root.sortierungGeneratedDocuments.push({
						id: attachmentId,
						label: attachment.internal_filename ?? ""
					});
				} else if (attachmentsById[attachmentId] !== undefined) {
					const attachment = attachmentsById[attachmentId];
					draft.document.root.sortierungAttachments.push({
						id: attachmentId,
						label: attachment.internal_filename ?? ""
					});
				}
			}
			for (const attachmentId of removedAttachments) {
				if (generatedDocumentsById[attachmentId] !== undefined) {
					draft.document.root.sortierungGeneratedDocuments =
						draft.document.root.sortierungGeneratedDocuments?.filter(it => it.id !== attachmentId);
				} else if (attachmentsById[attachmentId] !== undefined) {
					draft.document.root.sortierungAttachments = draft.document.root.sortierungAttachments?.filter(
						it => it.id !== attachmentId
					);
				}
			}
			for (const attachmentId of addedInitialAttachments) {
				const attachment = initialAttachmentsById[attachmentId];
				draft.document.root.sortierungInitialAttachments.push({
					id: attachmentId,
					label: attachment.internal_filename ?? ""
				});
			}
			for (const attachmentId of removedInitialAttachments) {
				draft.document.root.sortierungInitialAttachments =
					draft.document.root.sortierungInitialAttachments?.filter(it => it.id !== attachmentId);
			}
		});
		yield* put(ActivityActions.setData({ activityId, data: updatedDocument }));
	});
}
